<template>
  <div>
    <div class="flex flex-row justify-between items-center ">
      <h1 class="font-semibold leading-none">
        Row Count Over Time
      </h1>
      <FloatLabel
        style="width: 50%"
        variant="on"
      >
        <Select
          v-model="objectValue"
          class="w-full"
          :options="props.objects"
          size="small"
          filter
          option-label="label"
          option-value="value"
          input-id="objects-label"
        />
        <label for="objects-label">Object</label>
      </FloatLabel>
    </div>

    <div class="flex flex-col lg:flex-row py-3 gap-4 justify-end">
      <SelectButton
        v-model="timeFrame"
        class="flex justify-center w-full"
        option-label="label"
        option-value="value"
        :options="timeFrames"
        :allow-empty="true"
        @change="$emit('change-time-frame',timeFrame)"
      />
    </div>
    <Chart
      v-if="Object.keys(syncStats).length"
      type="line"
      :data="chartData"
      :options="chartOptions"
      class="h-[27rem] w-full"
    />
    <div
      v-else
      class="flex flex-1 w-full h-[30rem] items-center justify-center"
    >
      <ProgressSpinner />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useDark } from '@vueuse/core'

const darkMode = useDark()
const syncStats = defineModel({ type: Object })
defineEmits(['change-time-frame'])
const props = defineProps({
  objects: {
    type: Array,
    default () {
      return []
    }
  },
  rows: {
    type: Array,
    default () {
      return []
    }
  }
})
const timeFrames = [{ label: 'Hour', value: 'hourly' }, { label: 'Day', value: 'daily' }, { label: 'Week', value: 'weekly' }]
const timeFrame = ref('weekly')
const objectValue = ref('all')
let labelsMap = {}

watch(
  timeFrame,
  (newValue, oldValue) =>
    newValue === null && setTimeout(() => (timeFrame.value = oldValue), 0)
)

const chartData = computed(() => {
  const stats = syncStats.value
  labelsMap = {}
  const datasetsMap = {}
  const sumDatasetMap = {}
  const fillerZerosMap = {}
  const statsReverse = [...stats.syncStats]
  // Stats come ordered by object and time frame
  statsReverse && statsReverse.reverse().forEach((item) => {
    labelsMap[item.starttime] = true
    if (!datasetsMap[item.object]) {
      datasetsMap[item.object] = []
      fillerZerosMap[item.object] = item.rowcount === 0 ? props.rows.find(el => el.objectName === item.object)?.numDbRows || 0 : item.rowcount
    }
    if (!sumDatasetMap[item.starttime]) {
      sumDatasetMap[item.starttime] = 0
    }

    let itemToSum = item.rowcount
    if (itemToSum > 0) {
      fillerZerosMap[item.object] = itemToSum + item.deleterowcount - item.newrowcount
    } else {
      itemToSum = fillerZerosMap[item.object]
    }
    datasetsMap[item.object].unshift(itemToSum)
    sumDatasetMap[item.starttime] += itemToSum
  })

  const datasets = objectValue.value === 'all'
    ? [{
        label: 'All',
        data: Object.values(sumDatasetMap).reverse(),
        fill: false,
        tension: 0.1
      }
      ]
    : []
  Object.keys(datasetsMap).forEach(object => {
    if (objectValue.value === object) {
      datasets.push({
        label: object,
        data: datasetsMap[object],
        fill: false,
        tension: 0.1
      })
    }
  })
  return {
    labels: Object.keys(labelsMap).reverse().map(el => formatDate(el, stats.timeFrame)),
    datasets,
    responsive: true
  }
})

const chartOptions = computed(() => {
  return {
    stacked: false,
    maintainAspectRatio: false,
    aspectRatio: 0.6,
    responsive: true,
    plugins: {
      legend: {
        labels: darkMode.value
          ? {
              color: 'rgb(204, 204, 204)'
            }
          : undefined,
        position: 'top',
        onClick: null
      }
    },
    scales: darkMode.value
      ? {
          x: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          },
          y: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          }
        }
      : undefined
  }
})

const formatDate = (date, timeFrame) => {
  const d = new Date(date)
  if (timeFrame === 'daily') {
    return d.toLocaleTimeString('en-IT', { weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: true })
  } else if (timeFrame === 'weekly') {
    return d.toLocaleDateString('en-IT', { weekday: 'short', month: 'short', day: 'numeric' })
  } else if (timeFrame === 'hourly') {
    return d.toLocaleTimeString('en-IT', { hour: 'numeric', minute: 'numeric', hour12: true })
  }
}
</script>
